'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { getAccessToken } from '@/lib/authStorage';
import { log } from '@/lib/logger';
import { publicRuntimeConfig } from '@/lib/runtimeConfig';

// Типы событий WebSocket
export interface WsNewMessage {
  type: 'dm' | 'dm_sent' | 'room';
  fromUserId?: number;
  toUserId?: number;
  roomId?: number;
  message: {
    id: number;
    message: string;
    created_at: string;
    from_user_id: number;
    to_user_id?: number;
    room_id?: number;
    [key: string]: unknown;
  };
}

export interface WsUserTyping {
  userId: number;
  roomId?: number;
  isTyping: boolean;
}

export interface WsUserStatus {
  userId: number;
  isOnline: boolean;
}

export interface WsMessageRead {
  byUserId: number;
  messageId: number;
}

export interface WsMessageEdited {
  messageId: number;
  newText: string;
  fromUserId?: number;
  toUserId?: number;
}

export interface WsMessageDeleted {
  messageId: number;
  fromUserId?: number;
  toUserId?: number;
}

export interface WsMessageReaction {
  messageId: number;
  fromUserId?: number;
  toUserId?: number;
  emoji: string;
  action: 'add' | 'remove';
}

export interface WsMatchPing {
  matchId: string;
  userId: number;
  roomId: string;
  hotspotId?: string | null;
  label: string;
  createdAt: string;
}

export interface WsMatchChat {
  matchId: string;
  userId: number;
  body: string;
}

export interface WsMatchState {
  matchId: string;
  state: unknown;
}

export interface WsMatchResult {
  matchId: string;
  result: unknown;
}

// Тип контекста
interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  // Методы для отправки событий
  sendTyping: (toUserId?: number, roomId?: number, isTyping?: boolean) => void;
  joinRoom: (roomId: number) => void;
  leaveRoom: (roomId: number) => void;
  joinMatch: (matchId: string | number) => void;
  leaveMatch: (matchId: string | number) => void;
  sendMatchPing: (payload: { matchId: string | number; roomId: string; hotspotId?: string | null; label?: string }) => void;
  sendMatchChat: (payload: { matchId: string | number; body: string }) => void;
  markRead: (toUserId: number, messageId: number) => void;
  // Подписки на события
  onNewMessage: (handler: (data: WsNewMessage) => void) => () => void;
  onUserTyping: (handler: (data: WsUserTyping) => void) => () => void;
  onUserStatus: (handler: (data: WsUserStatus) => void) => () => void;
  onMessageRead: (handler: (data: WsMessageRead) => void) => () => void;
  onMessageEdited: (handler: (data: WsMessageEdited) => void) => () => void;
  onMessageDeleted: (handler: (data: WsMessageDeleted) => void) => () => void;
  onMessageReaction: (handler: (data: WsMessageReaction) => void) => () => void;
  onMatchPing: (handler: (data: WsMatchPing) => void) => () => void;
  onMatchChat: (handler: (data: WsMatchChat) => void) => () => void;
  onMatchState: (handler: (data: WsMatchState) => void) => () => void;
  onMatchResult: (handler: (data: WsMatchResult) => void) => () => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

const WS_URL = publicRuntimeConfig.websocketUrl;
const WS_ENABLED = publicRuntimeConfig.websocketEnabled;

export function SocketProvider({ children }: { children: ReactNode }) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const connectionAttemptedRef = useRef(false);

  // Инициализация соединения
  useEffect(() => {
    // WebSocket отключен — не подключаемся
    if (!WS_ENABLED) {
      return;
    }

    const token = getAccessToken();
    if (!token) {
      // Не авторизован — не подключаемся
      return;
    }

    // Предотвращаем повторные попытки после неудачи
    if (connectionAttemptedRef.current) {
      return;
    }
    connectionAttemptedRef.current = true;

    const newSocket = io(WS_URL, {
      path: '/socket.io/',
      auth: { token },
      // Сначала polling (надёжнее через прокси), затем upgrade до websocket
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    newSocket.on('connect', () => {
      log('info', 'ws-client', 'Connected to websocket server', { url: WS_URL });
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      log('info', 'ws-client', 'Disconnected from websocket server', { reason });
      setIsConnected(false);
    });

    newSocket.on('connect_error', (err) => {
      // Тихая ошибка — WebSocket опционален, мессенджер работает через polling API
      if (publicRuntimeConfig.debug) {
        log('warn', 'ws-client', 'Websocket server unavailable, falling back to polling', {
          reason: err.message,
        });
      }
      setIsConnected(false);
    });

    return () => {
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      connectionAttemptedRef.current = false;
    };
  }, []);

  // Переподключение при смене токена
  useEffect(() => {
    const handleAuthChange = () => {
      const token = getAccessToken();
      if (socketRef.current) {
        if (token) {
          socketRef.current.auth = { token };
          if (!socketRef.current.connected) {
            socketRef.current.connect();
          }
        } else {
          socketRef.current.disconnect();
        }
      }
    };

    window.addEventListener('lisad_auth_changed', handleAuthChange);
    return () => window.removeEventListener('lisad_auth_changed', handleAuthChange);
  }, []);

  // Методы отправки
  const sendTyping = useCallback((toUserId?: number, roomId?: number, isTyping = true) => {
    socketRef.current?.emit('typing', { toUserId, roomId, isTyping });
  }, []);

  const joinRoom = useCallback((roomId: number) => {
    socketRef.current?.emit('join_room', roomId);
  }, []);

  const leaveRoom = useCallback((roomId: number) => {
    socketRef.current?.emit('leave_room', roomId);
  }, []);

  const joinMatch = useCallback((matchId: string | number) => {
    socketRef.current?.emit('join_match', matchId);
  }, []);

  const leaveMatch = useCallback((matchId: string | number) => {
    socketRef.current?.emit('leave_match', matchId);
  }, []);

  const sendMatchPing = useCallback((payload: { matchId: string | number; roomId: string; hotspotId?: string | null; label?: string }) => {
    socketRef.current?.emit('match_ping', payload);
  }, []);

  const sendMatchChat = useCallback((payload: { matchId: string | number; body: string }) => {
    socketRef.current?.emit('match_chat', payload);
  }, []);

  const markRead = useCallback((toUserId: number, messageId: number) => {
    socketRef.current?.emit('mark_read', { toUserId, messageId });
  }, []);

  // Фабрика подписок на события
  const createEventSubscriber = <T,>(eventName: string) => {
    return (handler: (data: T) => void) => {
      const s = socketRef.current;
      if (!s) return () => {};
      s.on(eventName, handler);
      return () => { s.off(eventName, handler); };
    };
  };

  const onNewMessage = useCallback(createEventSubscriber<WsNewMessage>('new_message'), []);
  const onUserTyping = useCallback(createEventSubscriber<WsUserTyping>('user_typing'), []);
  const onUserStatus = useCallback(createEventSubscriber<WsUserStatus>('user_status'), []);
  const onMessageRead = useCallback(createEventSubscriber<WsMessageRead>('message_read'), []);
  const onMessageEdited = useCallback(createEventSubscriber<WsMessageEdited>('message_edited'), []);
  const onMessageDeleted = useCallback(createEventSubscriber<WsMessageDeleted>('message_deleted'), []);
  const onMessageReaction = useCallback(createEventSubscriber<WsMessageReaction>('message_reaction'), []);
  const onMatchPing = useCallback(createEventSubscriber<WsMatchPing>('match_ping'), []);
  const onMatchChat = useCallback(createEventSubscriber<WsMatchChat>('match_chat'), []);
  const onMatchState = useCallback(createEventSubscriber<WsMatchState>('match_state'), []);
  const onMatchResult = useCallback(createEventSubscriber<WsMatchResult>('match_result'), []);

  const value: SocketContextValue = {
    socket,
    isConnected,
    sendTyping,
    joinRoom,
    leaveRoom,
    joinMatch,
    leaveMatch,
    sendMatchPing,
    sendMatchChat,
    markRead,
    onNewMessage,
    onUserTyping,
    onUserStatus,
    onMessageRead,
    onMessageEdited,
    onMessageDeleted,
    onMessageReaction,
    onMatchPing,
    onMatchChat,
    onMatchState,
    onMatchResult,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

// Хук для использования WebSocket
export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket должен использоваться внутри SocketProvider');
  }
  return context;
}

// Хук для подписки на новые сообщения
export function useNewMessages(handler: (data: WsNewMessage) => void) {
  const { onNewMessage } = useSocket();
  useEffect(() => onNewMessage(handler), [onNewMessage, handler]);
}

// Хук для отслеживания статуса typing
export function useTypingStatus(handler: (data: WsUserTyping) => void) {
  const { onUserTyping } = useSocket();
  useEffect(() => onUserTyping(handler), [onUserTyping, handler]);
}

// Хук для отслеживания онлайн-статуса пользователей
export function useOnlineStatus(handler: (data: WsUserStatus) => void) {
  const { onUserStatus } = useSocket();
  useEffect(() => onUserStatus(handler), [onUserStatus, handler]);
}
